import { useEffect, useState, type JSX } from 'react';
import { usePostHog } from '@posthog/react';
import { Badge, Button } from '@viniciomendesr/rayrovisk-design-system';

/**
 * Which button was pressed. Required rather than optional so a new call site
 * has to name itself: an unlabelled copy is an event that says something was
 * taken and not from where, which is the only part worth knowing.
 */
export type CopySurface =
  | 'library'
  | 'assembled'
  | 'script'
  | 'pin'
  | 'pin-list'
  | 'pin-refreshed';

export interface PreparedClipboardCopy {
  text: string;
  placeholderTotal?: number;
  placeholderFilled?: number;
}

export interface CopyToClipboardProps {
  text: string;
  surface: CopySurface;
  /** The prompt's slug or the pin's id, when the caller holds one. */
  sourceRef?: string;
  label?: string;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  /**
   * `inline` (default) — the confirmation badge sits beside the button, in
   * flow, the way every existing caller draws it.
   *
   * `in-button` — the button says it itself, for a caller whose layout has a
   * fixed footprint. A badge cannot fit: beside the button it grows the row,
   * and floating above it escapes the card into the gap between rows.
   */
  confirmation?: 'inline' | 'in-button';
  prepareCopy?: () => PreparedClipboardCopy;
  onCopySuccess?: () => void;
}

/**
 * The interaction the whole Prompt Pack exists for, so it is one component
 * rather than five copies. The confirmation is a badge beside the button, the
 * way the mockup draws it, and it clears itself — a toast would cover the
 * prompt she just copied.
 */
export function CopyToClipboard({
  text,
  surface,
  sourceRef,
  label = 'Copiar prompt',
  variant = 'primary',
  size = 'sm',
  confirmation = 'inline',
  prepareCopy,
  onCopySuccess
}: CopyToClipboardProps): JSX.Element {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const posthog = usePostHog();
  const inButton = confirmation === 'in-button';

  useEffect(() => {
    if (state === 'idle') {
      return;
    }
    const timer = setTimeout(() => setState('idle'), 2400);
    return () => clearTimeout(timer);
  }, [state]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: inButton ? 0 : 10,
        flexWrap: inButton ? 'nowrap' : 'wrap',
        minWidth: 0
      }}
    >
      {!inButton && state === 'copied' && (
        <Badge tone="success" variant="subtle" size="sm">
          Copiado para a área de transferência
        </Badge>
      )}
      {!inButton && state === 'failed' && (
        <Badge tone="warning" variant="subtle" size="sm">
          Não deu para copiar. Selecione o texto e copie na mão.
        </Badge>
      )}
      <Button
        variant={variant}
        size={size}
        onClick={() => {
          const prepared = prepareCopy?.() ?? { text };
          // A clipboard write can be refused by the browser — no permission, no
          // secure context. Saying so beats a button that looks like it worked.
          navigator.clipboard
            .writeText(prepared.text)
            .then(() => {
              setState('copied');
              onCopySuccess?.();
              // The whole Prompt Pack is bought to press this. Everything else
              // measured on the way here — the search, the category, the open
              // — is a step towards it, so it is the one event that says the
              // product did its job.
              posthog?.capture('content_copied', {
                surface,
                source_ref: sourceRef,
                text_length: prepared.text.length,
                ...(prepared.placeholderTotal === undefined
                  ? {}
                  : { placeholder_total: prepared.placeholderTotal }),
                ...(prepared.placeholderFilled === undefined
                  ? {}
                  : { placeholder_filled: prepared.placeholderFilled })
              });
            })
            .catch(() => {
              setState('failed');
              // A browser refusing the clipboard is a buyer who paid and left
              // with nothing. Rare enough to be invisible without counting it.
              posthog?.capture('content_copy_failed', { surface });
            });
        }}
      >
        {inButton && state === 'copied' ? 'Copiado' : inButton && state === 'failed' ? 'Não copiou' : label}
      </Button>
      {/* The button has room for two words; a screen reader has room for the
          sentence, including what to do when the clipboard refused. */}
      {inButton && (
        <span
          role="status"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap'
          }}
        >
          {state === 'copied'
            ? 'Copiado para a área de transferência'
            : state === 'failed'
              ? 'Não deu para copiar. Abra o fixado e copie o texto na mão.'
              : ''}
        </span>
      )}
    </div>
  );
}
